import { Injectable, isDevMode } from '@angular/core';

/**
 * Per-domain runtime configuration.
 *
 * These values differ per domain/subdomain and are owned ENTIRELY by this
 * service — they are NOT in `environment.*.ts` and are NOT build-time `.env`
 * vars. They are loaded at startup from `/config.json`. NGINX serves a different
 * `/config.json` per `server_name`, so a single Angular build can be deployed to
 * many domains. Consumers must read these via `ConfigService` (never from
 * `environment`). Extend this interface if more keys need to become
 * runtime-overridable later. See docs/RUNTIME_CONFIG.md.
 *
 * NOTE: `encryptedKey` is deliberately NOT here — it stays a build-time value in
 * `environment.*.ts` (from `.env` `NG_APP_ENCRYPTED_KEY`).
 */
export interface AppRuntimeConfig {
  recaptchaSiteKey: string;
  orgId: string;
  acceptConsentKey: string;
}

/** Safe defaults until `/config.json` is loaded (or if it fails to load). */
const DEFAULT_CONFIG: AppRuntimeConfig = {
  recaptchaSiteKey: '',
  orgId: '',
  acceptConsentKey: ''
};

/** Keys read from `/config.json`. */
const RUNTIME_KEYS = Object.keys(DEFAULT_CONFIG) as (keyof AppRuntimeConfig)[];

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private config: AppRuntimeConfig = { ...DEFAULT_CONFIG };
  private loaded = false;

  /**
   * Fetches `/assets/config/config.json` and stores present, non-empty keys. Runs via
   * `provideAppInitializer`, so it completes before the app bootstraps and
   * before any consumer reads a config value.
   *
   * Uses native `fetch` (not `HttpClient`) so the token/error/cache
   * interceptors don't fire on this static file. On any failure it logs a
   * warning and leaves the empty defaults in place so the app still boots
   * (a valid `/config.json` is required in real deployments).
   */
  async load(): Promise<void> {
    try {
      const configUrl = new URL(
        'assets/config/config.json',
        document.baseURI
      ).toString();
      const res = await fetch(configUrl, { cache: 'no-store' });
      if (!res.ok) {
        throw new Error(`config.json HTTP ${res.status}`);
      }

      const cfg = (await res.json()) as Partial<AppRuntimeConfig>;

      const applied: string[] = [];
      for (const key of RUNTIME_KEYS) {
        const value = cfg[key];
        if (value) {
          this.config[key] = value;
          applied.push(key);
        }
      }

      this.loaded = true;
      // Dev-only info log. Shows values as a table to aid local debugging — this
      // branch never runs in production builds, and these keys are client-visible
      // anyway.
      if (isDevMode()) {
        console.log(
          '[ConfigService] /assets/config/config.json loaded. Set from config:',
          applied.length
            ? applied.join(', ')
            : '(none — keys missing/empty, using empty defaults)'
        );
      }
    } catch (err) {
      // Non-fatal for boot (the app still starts on empty defaults), but it is a
      // real misconfiguration in any deployment — surface it as an error.
      console.error(
        [
          '[ConfigService] Failed to load runtime config from /assets/config/config.json.',
          'The app is running on EMPTY defaults — reCAPTCHA and consent will not work.',
          'Fix: ensure the file is deployed and served for this domain.',
          'Local dev: copy public/assets/config/config.example.json to',
          'public/assets/config/config.json and fill in real values.'
        ].join('\n'),
        err
      );
    }
  }

  get recaptchaSiteKey(): string {
    return this.config.recaptchaSiteKey;
  }

  get orgId(): string {
    return this.config.orgId;
  }

  get acceptConsentKey(): string {
    return this.config.acceptConsentKey;
  }

  get isLoaded(): boolean {
    return this.loaded;
  }
}
